import { makeAutoObservable, runInAction } from 'mobx';
import ShopifySettingsService from '../../../../services/api/shopify-settings-service';
import { IShopifySettings } from '../../../../models/IntegrationsTypes';

import { rootContainer, TYPE } from '../../../../services/container/root';
import { ISaveable, WithOld } from '../../../../stores/types';
import { IIframeService } from '../../../../services/iframe-service/iframe-service';
import dashboardSettingsStore from "../../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../../models/ProfileCapabilities";
import currentPortalStore from "../../../../stores/current-portal-store";
import { RESTOCK_BY } from "../../../../models/User";

interface ICustomPortalUrlError {
    message: string;
    invalidValue: string;
}

class ShopifySettingsStore implements ISaveable {
    private _model: IShopifySettings = {} as IShopifySettings;
    private _changes: IShopifySettings = {} as IShopifySettings;
    private _isSaving: boolean = false;
    private _isLoading: boolean = false;
    /** _autoTrackEnabledChanged is used to indicate server wether it needs to register/unregoster fulfillments webhooks */
    private _autoTrackEnabledChanged: boolean = false;
    /** _integrationEnabledChanged is used to indicate server wether it needs to register/unregoster returnGo scripts */
    private _integrationEnabledChanged: boolean = false;
    /** _customPortalUrlError is used to store the error message and the invalid value for the custom portal url */
    private _custom_portal_url_error: ICustomPortalUrlError | null = null;

    public get isIntegrationEnabled() {
        return this._changes.is_integration_enabled ?? this._model.is_integration_enabled;
    }

    public get returnPortalUrl() { return this.storeDomain + this.proxyPath; }
    public get returnPortalPath() { return this._changes.return_portal_path ?? this._model.return_portal_path ?? ''; }
    public get trackingPortalPath() { return this._changes.tracking_portal_path ?? this._model.tracking_portal_path ?? ''; }
    public get proxyPath() { return this._changes.proxy_path ?? this._model.proxy_path; }
    public get allowedForEnable() { return this._model.is_policies_enabled; }
    public get storeDomain() { return this._model.store_domain; }
    public get isAutomaticallyTrackAllOrders() { return this._changes.auto_track_store_orders ?? this._model.auto_track_store_orders; }
    public get isUseCustomReturnsUrl() { return this._changes.use_custom_return_portal_url ?? this._model.use_custom_return_portal_url }
    public get isUseCustomTrackingPortalUrl() { return this._changes.use_custom_tracking_portal_url ?? this._model.use_custom_tracking_portal_url }
    public get isUseShopifyReturnsApi() { return this._changes.use_shopify_returns_api ?? this._model.use_shopify_returns_api }
    public get constructedReturnPortalUrl() {
        let path = this.returnPortalPath;
        if ((path + '').trim() !== '' && !this.returnPortalPath?.startsWith('/')) {
            path = '/' + path;
        }
        return `${this.storeDomain}${this.proxyPath}${path}`;
    }
    public get constructedTrackingPortalUrl() {
        let path = this.trackingPortalPath;
        if ((path + '').trim() !== '' && !this.returnPortalPath?.startsWith('/')) {
            path = '/' + path;
        }
        return `${this.storeDomain}${this.proxyPath}${path}`;
    }

    public get customReturnPortalUrl() { return this._changes.custom_return_portal_url ?? this._model.custom_return_portal_url; }
    public setCustomReturnPortalUrl(v: string): void { this._changes.custom_return_portal_url = v; }

    public get customTrackingPortalUrl() { return this._changes.custom_tracking_portal_url ?? this._model.custom_tracking_portal_url; }
    public setCustomTrackingPortalUrl(v: string): void { this._changes.custom_tracking_portal_url = v; }


    /** Don't have special field in DB for this.
     * Instead we're retrieving the first part of proxy_path "/proxy-prefix/app-name" */
    public get appName(): string {
        if (this.proxyPath) {
            let appName=this.proxyPath;
            
            // Remove the path prefix and trailing slash
            appName = appName.replace(this.proxySubPathPrefix+'/','');
            return appName;
        }
        return '';
    }

    public get proxySubPathPrefix(): string {
        return this.proxyPath?.match(/^\/\w+/)?.[0] ?? '';
    }

    public get isLoading(): boolean {
        return this._isLoading;
    }

    private get iframeService(): IIframeService {
        return rootContainer.get<IIframeService>(TYPE.IframService);
    }

    private get ShopifySettingsService(): ShopifySettingsService {
        return rootContainer.get<ShopifySettingsService>(TYPE.ShopifySettingsService);
    }

    /** 
     * CustomPortalUrlErrorMessage is used to show the error message for the custom portal url,
     * it returns the error message if the custom portal url is invalid, otherwise it returns null.
     * Verifies actual value against the requested value to avoid showing the error message, 
     * when the user is typing value different from invalid value.
     */
    public get customPortalUrlErrorMessage(): string | null {
        if (
            !this._custom_portal_url_error
            || this._custom_portal_url_error.invalidValue !== this.customReturnPortalUrl
        ) {
            return null;
        }
        return this._custom_portal_url_error.message;
    }

    constructor() {
        makeAutoObservable(this);
    }

    async load() {
        this._isLoading = true;
        try {
            const shopifySettings = await this.ShopifySettingsService.getShopifySettings();
            const alignedModel = this.ensureValidModelCopy(shopifySettings);
    
            runInAction(() => {
                this._model = alignedModel;
                this._isLoading = false;
            });
        } catch (e) {
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }

    public setIsIntegrationEnabled(v: boolean) {
        if (v && !this._model.is_policies_enabled) {
            /** prevent from enabling integration when no enabled policies */
            return;
        }

        this._changes.is_integration_enabled = v;
        this._integrationEnabledChanged = true;
        this._clearChangesIfEqual('is_integration_enabled');
    }

    public setIsAutomaticallyOrderTracking(v: boolean) {
        this._changes.auto_track_store_orders = v;
        this._autoTrackEnabledChanged = true;
        this._clearChangesIfEqual('auto_track_store_orders');
    }

    public setUseCustomReturnPortalUrl(v: boolean) {
        this._changes.use_custom_return_portal_url = v;
        this._clearChangesIfEqual('use_custom_return_portal_url');
    }

    public setUseCustomTrackingPortalUrl(v: boolean) {
        this._changes.use_custom_tracking_portal_url = v;
        this._clearChangesIfEqual('use_custom_tracking_portal_url');
    }

    public setAppName(value: string) {
        this._changes.proxy_path = this.proxySubPathPrefix  +'/' + value;
        this._clearChangesIfEqual('proxy_path');
    }

    public setProxy(value: string) {
        this._changes.proxy_path = value + '/' + this.appName;
        this._clearChangesIfEqual('proxy_path');
    }

    public setProxyPath(value: string) {
        this._changes.proxy_path = value;
        this._clearChangesIfEqual('proxy_path');
    }

    public setReturnPortalPath(value: string) {
        this._changes.return_portal_path = value;
        this._clearChangesIfEqual('return_portal_path');
    }

    public setTrackingPortalPath(value: string) {
        this._changes.tracking_portal_path = value;
        this._clearChangesIfEqual('tracking_portal_path');
    }

    public setUseShopifyReturnsApi(value: boolean) {
		const shopifyIntegrationCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.SHOPIFY_INTEGRATION);
		if (shopifyIntegrationCapability.enableShopifyReturnsForcedValue === null) {
			this._changes.use_shopify_returns_api = value;
		} else {
			this._changes.use_shopify_returns_api = shopifyIntegrationCapability.enableShopifyReturnsForcedValue;
		}
        this._clearChangesIfEqual('use_shopify_returns_api');

        const shopifyReturnsEnabled = this.isUseShopifyReturnsApi;
        const storeSetting = currentPortalStore.setting;
        if (!shopifyReturnsEnabled && storeSetting?.restockBy === RESTOCK_BY.RESTOCK_BY_ORDER_DISPOSAL) {
            storeSetting.setRestockBy(RESTOCK_BY.RESTOCK_BY_INCREASING_INVENTORY);
        }
    }

    private _clearChangesIfEqual(key: keyof IShopifySettings): void {
        if (this._changes[key] === this._model?.[key]) {
            delete this._changes[key];
        }
    }

    /********* ISaveable implementation *************/
    public isSaving(): boolean {
        return this._isSaving;
    }

    public hasChanges(): boolean {
        return !!Object.keys(this._changes).length;
    }

    public commitChanges() {
        this._model = {...this._model, ...this._changes};
        this._changes = {} as IShopifySettings;
    }

    public getUpdatedModel(): IShopifySettings {
        return {
            ...this._model,
            ...this._changes,
            _auto_track_store_orders_changed: this._autoTrackEnabledChanged,
            _integration_enabled_changed: this._integrationEnabledChanged,
        };
    }

    public async save() {
        this._isSaving = true;
        this._custom_portal_url_error = null;
        const updatedModel: WithOld<IShopifySettings> = this.getUpdatedModel();
        updatedModel.__old = this._model;
        try {
            const portalURLValidationResponse = await this.ShopifySettingsService.validateCustomReturnPortalUrl(updatedModel);
            if (portalURLValidationResponse.is_valid) {
                await this.ShopifySettingsService.saveShopifySettings(updatedModel);
            } else {
                this._custom_portal_url_error = {
                    message: portalURLValidationResponse.message ?? '',
                    invalidValue: this.customReturnPortalUrl,
                };
                runInAction(() => {
                    this._isSaving = false;
                });
                return;
            }
            runInAction(() => {
                this.commitChanges();
                this._isSaving = false;
                this._autoTrackEnabledChanged = false;
                this._integrationEnabledChanged = false;
            });

            /** temp solution for the new users progress bar */
            this.iframeService.refreshParentFrame();
        } catch (err) {            
            runInAction(() => {
                this._isSaving = false;
            });
        }
    }

    private ensureValidModelCopy(data: IShopifySettings): IShopifySettings {
        const toStr = (v: any) => !!v?.toString ? v.toString() : '';
        const toBool = (v: undefined | any) => Boolean(v);

        return {
            ...data,
            return_portal_path: toStr(data.return_portal_path),
            tracking_portal_path: toStr(data.tracking_portal_path),
            custom_return_portal_url: toStr(data.custom_return_portal_url),
            custom_tracking_portal_url: toStr(data.custom_tracking_portal_url),
            auto_track_store_orders: toBool(data.auto_track_store_orders),
            use_custom_return_portal_url: toBool(data.use_custom_return_portal_url),
            use_custom_tracking_portal_url: toBool(data.use_custom_tracking_portal_url),
            use_shopify_returns_api: toBool(data.use_shopify_returns_api),
        };
    }
}

export default new ShopifySettingsStore();

